<template>
  <div v-if="items && items.length > 0">
    <div class="flex flex-col gap-2">
      <div
        v-for="requirement in sortedItems"
        :key="requirement.createdAt"
        class="group rounded-[20px] border shadow-[0_2px_12px_rgba(0,0,0,0.04)] transition-shadow duration-200 transition-[border-color] duration-200"
        :class="cardClasses(requirement)">
        <div class="flex items-start gap-4 px-[18px] py-3.5 max-md:flex-col max-md:gap-2.5 max-md:px-3.5 max-md:py-3">
          <div class="min-w-0 flex-1">
            <p v-if="hideActions" class="m-0 text-sm font-medium leading-[1.4] text-ink">{{ requirement.title }}</p>
            <a v-else href="#" class="block text-sm font-medium leading-[1.4] text-ink no-underline hover:text-primary" @click.prevent="openRequirementModal(requirement)">{{ requirement.title }}</a>
            <p v-if="requirement.subtitle" class="mt-1 text-[13px] text-muted" v-html="linkify(requirement.subtitle)"></p>
            <div v-if="requirement.tasks && requirement.tasks.length > 0" class="mt-2">
              <div class="flex items-center gap-1 py-0.5 cursor-pointer select-none" v-for="(task, taskIndex) in requirement.tasks" :key="taskIndex" @click.stop="toggleTask(requirement, taskIndex)">
                <span class="inline-flex items-center justify-center w-4 h-4 rounded border shrink-0 transition-colors duration-150"
                  :class="task.isComplete ? 'bg-[#2d7a0e] border-[#2d7a0e]' : 'bg-white border-[#d0ccd5]'">
                  <svg v-if="task.isComplete" xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                </span>
                <span class="text-[13px]" :class="task.isComplete ? 'text-muted line-through' : 'text-ink'">{{ task.title }}</span>
              </div>
            </div>
          </div>

          <div class="flex shrink-0 items-center gap-4 max-md:flex-wrap max-md:gap-2.5">
            <div v-if="requirement.deadline" class="flex items-center gap-2 whitespace-nowrap text-[13px] text-muted">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
              {{ requirement.deadline | formatDate }}
            </div>

            <div v-if="requirement.assignedTo" class="flex items-center">
              <div v-for="person in getAssignedTo(requirement.assignedTo).split(',')" :key="person" class="-ml-1 first:ml-0">
                <img v-if="getPersonPhoto(person.trim())" class="size-6 rounded-full border-2 border-transparent object-cover" :class="personImgShadow(requirement)" width="24" :src="getPersonPhoto(person.trim())" :title="person.trim()" :alt="person.trim()">
              </div>
            </div>

            <div class="flex items-center gap-2 whitespace-nowrap text-[13px] text-muted">
              <a v-if="showPublicDocument && requirement.publicDocumentUrl" :href="requirement.publicDocumentUrl" target="_blank" class="inline-flex items-center gap-1 rounded-md bg-canvas px-2.5 py-1 text-[13px] font-medium text-primary no-underline transition-colors duration-150 hover:bg-[#ecedf3]">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                Open
              </a>
              <a v-else-if="!showPublicDocument && requirement.documentUrl" :href="requirement.documentUrl" target="_blank" class="inline-flex items-center gap-1 rounded-md bg-canvas px-2.5 py-1 text-[13px] font-medium text-primary no-underline transition-colors duration-150 hover:bg-[#ecedf3]">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                Open
              </a>
            </div>
          </div>

          <div class="flex shrink-0 items-center gap-3 max-md:w-full max-md:justify-between">
            <div v-if="!showPublicDocument" class="relative">
              <button
                v-if="!hideActions"
                class="whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium border-none cursor-pointer transition-opacity duration-150 hover:opacity-75"
                :class="statusClasses(requirement)"
                @click.stop="toggleStatusMenu(requirement)">
                {{ getStatus(requirement.status) }}
              </button>
              <span v-else class="whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium" :class="statusClasses(requirement)">{{ getStatus(requirement.status) }}</span>
              <div v-if="statusMenuCreatedAt === requirement.createdAt" class="absolute right-0 top-full mt-1 bg-white border border-black/6 rounded-[14px] shadow-[0_8px_24px_rgba(0,0,0,0.08)] z-50 py-1.5 min-w-[160px]">
                <button
                  v-for="status in applicationStatuses"
                  :key="status.value"
                  class="w-full flex items-center gap-2 px-3.5 py-2 text-[13px] text-left border-none bg-transparent cursor-pointer transition-colors duration-100 hover:bg-canvas"
                  :class="requirement.status === status.value ? 'text-primary font-semibold' : 'text-ink'"
                  @click.stop="changeStatus(requirement, status.value)">
                  <span class="w-2 h-2 rounded-full shrink-0" :style="{ background: statusDotColor(status.value) }"></span>
                  {{ status.text }}
                </button>
              </div>
            </div>

            <div v-if="!hideActions" class="flex gap-1 opacity-40 transition-opacity duration-150 group-hover:opacity-100 max-md:opacity-100">
              <button type="button" class="flex size-8 items-center justify-center rounded-lg text-muted bg-transparent border-0 cursor-pointer transition-colors duration-150 hover:bg-[#f4f5fa] hover:text-ink" @click.stop="openRequirementModal(requirement)" title="Edit" aria-label="Edit requirement">
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
              </button>
              <button type="button"
                class="flex items-center justify-center rounded-lg text-muted bg-transparent border-0 cursor-pointer transition-colors duration-150 hover:bg-[#fef2f2] hover:text-[#e53935]"
                :class="confirmDeleteCreatedAt === requirement.createdAt
                  ? 'w-auto px-2.5 text-xs font-semibold text-[#e53935]'
                  : 'size-8'"
                @click.stop="confirmDeleteRequirement(requirement)"
                title="Delete" aria-label="Delete requirement">
                <span v-if="confirmDeleteCreatedAt === requirement.createdAt">Delete?</span>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { PHOTOS } from '@/services/photos'
import { getAssignedToText } from '@/services/applicant-helpers'

export default {
  name: 'requirements-table',
  props: {
    items: {
      type: Array,
      required: true
    },
    applicant: {
      type: Object,
      default: null
    },
    hideActions: {
      type: Boolean,
      default: false
    },
    showPublicDocument: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      confirmDeleteCreatedAt: null,
      statusMenuCreatedAt: null
    }
  },
  computed: {
    ...mapState({
      managers: state => state.managers.managers,
      applicationStatuses: state => state.components.applicationStatuses
    }),
    sortedItems () {
      return [...this.items].sort((a, b) => {
        const dA = a.deadline ? new Date(a.deadline).getTime() : Infinity
        const dB = b.deadline ? new Date(b.deadline).getTime() : Infinity
        return dA - dB
      })
    }
  },
  methods: {
    cardClasses (requirement) {
      const isComplete = (requirement.status === 'complete' || requirement.status === 'sentToApplicant') && !this.showPublicDocument
      const isProgress = (requirement.status === 'collecting' || requirement.status === 'inProgress') && !this.showPublicDocument

      if (isComplete) {
        return 'bg-[#f6fbf2] border-[rgba(45,122,14,0.15)] shadow-[0_1px_3px_rgba(45,122,14,0.06)]'
      }
      if (isProgress) {
        return 'bg-[#fffaf5] border-[rgba(212,160,84,0.2)] shadow-[0_1px_3px_rgba(212,160,84,0.06)]'
      }
      return 'bg-white border-black/6 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:border-black/8 hover:shadow-[0_4px_12px_rgba(0,0,0,0.07)]'
    },
    statusClasses (requirement) {
      const isComplete = requirement.status === 'complete' || requirement.status === 'sentToApplicant'
      const isProgress = requirement.status === 'collecting' || requirement.status === 'inProgress' || requirement.status === 'toCheck'

      if (isComplete) {
        return 'text-[#2d7a0e] bg-[#e2f3d1]'
      }
      if (isProgress) {
        return 'text-[#8a6d3b] bg-[#fffaf5]'
      }
      return 'text-muted bg-[#f4f5fa]'
    },
    personImgShadow (requirement) {
      const isComplete = (requirement.status === 'complete' || requirement.status === 'sentToApplicant') && !this.showPublicDocument
      const isProgress = (requirement.status === 'collecting' || requirement.status === 'inProgress') && !this.showPublicDocument

      if (isComplete) {
        return 'shadow-[0_0_0_2px_#f6fbf2]'
      }
      if (isProgress) {
        return 'shadow-[0_0_0_2px_#fffaf5]'
      }
      return 'shadow-[0_0_0_2px_#fff]'
    },
    linkify (text) {
      if (!text) return ''
      const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      return escaped.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" class="text-primary underline hover:opacity-80">$1</a>')
    },
    getAssignedTo (requirement) {
      return getAssignedToText(requirement, this.managers)
    },
    getStatus (status) {
      const statusItem = this.applicationStatuses.filter((item) => {
        return item.value === status
      })[0]
      return statusItem ? statusItem.text : '-'
    },
    getPersonPhoto (name) {
      return PHOTOS[name.toLowerCase()] || null
    },
    toggleStatusMenu (requirement) {
      if (this.statusMenuCreatedAt === requirement.createdAt) {
        this.statusMenuCreatedAt = null
      } else {
        this.statusMenuCreatedAt = requirement.createdAt
      }
    },
    changeStatus (requirement, newStatus) {
      this.statusMenuCreatedAt = null
      if (!this.applicant || !this.applicant.id || requirement.status === newStatus) return
      const oldStatus = requirement.status
      const requirements = this.applicant.requirements.map(r => {
        if (r.createdAt !== requirement.createdAt) return r
        return { ...r, status: newStatus }
      })
      this.$store.dispatch('UPDATE_APPLICANT_REQUIREMENTS', {
        applicantId: this.applicant.id,
        requirements
      }).then(() => {
        this.$emit('statusChanged', {
          requirement,
          from: oldStatus,
          to: newStatus
        })
        this.$emit('update')
      }).catch(() => {
        this.$store.dispatch('SHOW_TOAST', { message: 'Failed to update status', type: 'error' })
      })
    },
    statusDotColor (status) {
      const colors = {
        created: '#d6d8e3',
        collecting: '#d4a054',
        inProgress: '#3AA6DD',
        toCheck: '#7c5bea',
        complete: '#2d7a0e',
        sentToApplicant: '#1a8a6e'
      }
      return colors[status] || '#d6d8e3'
    },
    openRequirementModal (requirement) {
      this.$emit('openRequirementModal', requirement)
    },
    toggleTask (requirement, taskIndex) {
      if (!this.applicant || !this.applicant.id) return
      const requirements = this.applicant.requirements.map(r => {
        if (r.createdAt !== requirement.createdAt) return r
        const tasks = r.tasks.map((t, i) => {
          if (i !== taskIndex) return t
          return { ...t, isComplete: !t.isComplete }
        })
        return { ...r, tasks }
      })
      this.$store.dispatch('UPDATE_APPLICANT_REQUIREMENTS', {
        applicantId: this.applicant.id,
        requirements
      }).then(() => {
        this.$emit('update')
      }).catch(() => {
        this.$store.dispatch('SHOW_TOAST', { message: 'Failed to update task', type: 'error' })
      })
    },
    confirmDeleteRequirement (requirement) {
      if (this.confirmDeleteCreatedAt === requirement.createdAt) {
        this.confirmDeleteCreatedAt = null
        this.$emit('deleteRequirement', requirement.createdAt)
      } else {
        this.confirmDeleteCreatedAt = requirement.createdAt
      }
    }
  },
  mounted () {
    this._resetConfirmDelete = () => {
      this.confirmDeleteCreatedAt = null
      this.statusMenuCreatedAt = null
    }
    document.addEventListener('click', this._resetConfirmDelete)
  },
  beforeDestroy () {
    if (this._resetConfirmDelete) {
      document.removeEventListener('click', this._resetConfirmDelete)
    }
  }
}
</script>
